import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Link, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import { MotionConfig, motion } from "framer-motion";
import { Toaster } from "sonner";
import { House, Books, Users, Plus, SidebarSimple, CloudArrowUp, Graph } from "@phosphor-icons/react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import DriveConnect from "./components/DriveConnect";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { driveStatus } from "./lib/api";
import MeetingReminders from "./components/MeetingReminders";
import NativeBridge from "./components/NativeBridge";
import DriveAutoBackup from "./components/DriveAutoBackup";
import PipOverlay from "./components/PipOverlay";

// Route-level code splitting: each page loads as its own chunk on first
// visit, so the initial bundle carries only the shell. (PipOverlay stays
// eager — the pip window must paint instantly with no chunk fetch.)
const Home = lazy(() => import("./pages/Home"));
const LiveSession = lazy(() => import("./pages/LiveSession"));
const Review = lazy(() => import("./pages/Review"));
const Participants = lazy(() => import("./pages/Participants"));
const ParticipantDashboard = lazy(() => import("./pages/ParticipantDashboard"));
const Folder = lazy(() => import("./pages/Folder"));
const MeetingRoom = lazy(() => import("./pages/MeetingRoom"));
const Library = lazy(() => import("./pages/Library"));
const Brain = lazy(() => import("./pages/Brain"));
const Login = lazy(() => import("./pages/Login"));
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";
import { warmBackend } from "./lib/warmup";
import "./App.css";

// Wake the backend the moment the app boots, before any auth-gated request.
// Fire-and-forget — the rest of the app keeps rendering immediately.
warmBackend();

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Left sidebar — the native-app nav rail. Only renders once signed in; the
// login screen stays full-bleed. Collapsible to an icon rail. The top strip is
// the window drag region and (in the Tauri shell) clears the traffic lights.
function Sidebar({ collapsed, onToggle }) {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [driveOpen, setDriveOpen] = useState(false);
    const [driveConnected, setDriveConnected] = useState(false);
    useEffect(() => {
        if (!user) return undefined;
        let alive = true;
        driveStatus().then((s) => { if (alive) setDriveConnected(!!s?.connected); }).catch(() => {});
        return () => { alive = false; };
    }, [user, driveOpen]);
    if (!user) return null;
    const initial = (user.name || user.email || "?").charAt(0).toUpperCase();
    const row = (isActive) =>
        `flex items-center ${collapsed ? "justify-center w-9 mx-auto" : "gap-2.5 px-3"} h-9 rounded-lg text-[13.5px] transition-colors ${
            isActive ? "bg-[#0A0A0A]/[0.06] text-zinc-900 font-medium" : "text-[#52525B] hover:bg-[#0A0A0A]/[0.04] hover:text-zinc-900"
        }`;
    return (
        <aside
            className="fixed inset-y-0 left-0 w-[var(--sidebar-w)] z-40 flex flex-col border-r border-[#EFEFEF] bg-[#FBFBFC]/85 backdrop-blur app-drag transition-[width] duration-200 ease-out"
            data-testid="app-sidebar"
            data-tauri-drag-region
        >
            {/* Top strip — logo + collapse toggle. Drag region. */}
            <div className={`sidebar-top flex items-center gap-2 ${collapsed ? "justify-center px-0" : "px-3.5"}`} data-tauri-drag-region>
                <Link to="/" className="flex items-center gap-2.5 group no-drag min-w-0" data-testid="header-logo-link" aria-label="Lisn — home">
                    <div className="w-6 h-6 bg-[#0A0A0A] rounded-md flex items-center justify-center shrink-0 transition group-hover:bg-black">
                        <div className="w-1.5 h-1.5 bg-[#FF3333] rounded-full" />
                    </div>
                    {!collapsed && (
                        <div className="font-heading font-extrabold text-[16px] tracking-tight leading-none">
                            Lisn<span className="text-[#FF3333]">.</span>
                        </div>
                    )}
                </Link>
                {!collapsed && (
                    <button
                        type="button"
                        onClick={onToggle}
                        className="no-drag ml-auto shrink-0 w-7 h-7 rounded-md text-[#A1A1AA] hover:text-zinc-700 hover:bg-[#0A0A0A]/[0.05] flex items-center justify-center transition-colors"
                        title="Collapse sidebar"
                        data-testid="sidebar-collapse"
                    >
                        <SidebarSimple size={16} />
                    </button>
                )}
            </div>

            {/* Primary CTA — single clean button (no nested badge). */}
            <div className={`px-3 pb-2 ${collapsed ? "flex justify-center" : ""}`}>
                <button
                    type="button"
                    onClick={() => nav("/?new=1")}
                    title="Start Lisning"
                    className={`inline-flex items-center justify-center bg-[#0A0A0A] hover:bg-black text-white font-medium shadow-[0_1px_2px_rgba(0,0,0,0.06)] transition-colors ${
                        collapsed ? "w-10 h-10 rounded-xl" : "w-full gap-2 h-10 rounded-xl text-[13.5px]"
                    }`}
                    data-testid="sidebar-new-session"
                >
                    <Plus size={collapsed ? 18 : 15} weight="bold" />
                    {!collapsed && "Start Lisning"}
                </button>
            </div>

            {/* Nav */}
            <nav className="px-3 mt-1 flex flex-col gap-0.5" aria-label="Primary">
                <NavLink to="/" end className={({ isActive }) => row(isActive)} data-testid="nav-sessions" title="Home"><House size={17} weight="duotone" />{!collapsed && <span>Home</span>}</NavLink>
                <NavLink to="/library" className={({ isActive }) => row(isActive)} data-testid="nav-library" title="Library"><Books size={17} weight="duotone" />{!collapsed && <span>Library</span>}</NavLink>
                <NavLink to="/participants" className={({ isActive }) => row(isActive)} data-testid="nav-participants" title="People"><Users size={17} weight="duotone" />{!collapsed && <span>People</span>}</NavLink>
                <NavLink to="/brain" className={({ isActive }) => row(isActive)} data-testid="nav-brain" title="Second brain"><Graph size={17} weight="duotone" />{!collapsed && <span>Second brain</span>}</NavLink>
            </nav>

            {collapsed && (
                <button
                    type="button"
                    onClick={onToggle}
                    className="no-drag mx-auto mt-2 w-9 h-9 rounded-lg text-[#A1A1AA] hover:text-zinc-700 hover:bg-[#0A0A0A]/[0.05] flex items-center justify-center transition-colors"
                    title="Expand sidebar"
                    data-testid="sidebar-expand"
                >
                    <SidebarSimple size={16} />
                </button>
            )}

            {/* Bottom group — Drive sync + account, pinned to the bottom. */}
            <div className="mt-auto">
                <div className="px-3 pb-1">
                    <button
                        type="button"
                        onClick={() => setDriveOpen(true)}
                        title="Drive sync & backup"
                        data-testid="sidebar-drive-sync"
                        className={`flex items-center ${collapsed ? "justify-center w-9 h-9 mx-auto" : "gap-2.5 px-3 h-9 w-full"} rounded-lg text-[13.5px] text-[#52525B] hover:bg-[#0A0A0A]/[0.04] hover:text-zinc-900 transition-colors`}
                    >
                        <CloudArrowUp size={17} weight="duotone" />
                        {!collapsed && <span>Drive sync</span>}
                        {!collapsed && driveConnected && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-500" title="Connected" />}
                    </button>
                </div>

            {/* Footer — the whole row opens the account menu. */}
            <div className="p-3 border-t border-[#EFEFEF]">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            className={`no-drag w-full flex items-center ${collapsed ? "justify-center" : "gap-2.5"} rounded-lg p-1.5 hover:bg-[#0A0A0A]/[0.04] transition-colors focus:outline-none`}
                            data-testid="user-menu"
                            title={user.name || user.email || "Account"}
                        >
                            <span className="w-8 h-8 rounded-full bg-[#FAFAFA] border border-[#E5E5E5] overflow-hidden flex items-center justify-center shrink-0">
                                {user.picture ? (
                                    <img src={user.picture} alt={user.name || "user"} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                                ) : (
                                    <span className="text-xs font-medium text-[#0A0A0A]">{initial}</span>
                                )}
                            </span>
                            {!collapsed && (
                                <span className="min-w-0 leading-tight text-left">
                                    <span className="block text-[12.5px] font-medium text-zinc-900 truncate">{user.name || "Signed in"}</span>
                                    {user.email && <span className="block text-[11px] text-[#A1A1AA] truncate">{user.email}</span>}
                                </span>
                            )}
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" side="top" className="w-56 rounded-xl border-[#E5E5E5] mb-2">
                        <DropdownMenuLabel className="font-normal py-2">
                            <div className="text-sm font-medium text-[#0A0A0A] truncate">{user.name || "Signed in"}</div>
                            {user.email && <div className="text-xs text-[#71717A] truncate mt-0.5">{user.email}</div>}
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={logout} data-testid="logout-button" className="text-sm text-[#0A0A0A] cursor-pointer">
                            Sign out
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            </div>

            <Dialog open={driveOpen} onOpenChange={setDriveOpen}>
                <DialogContent className="rounded-[24px] sm:rounded-[24px] max-w-[520px] border-[#EFEFEF] p-7" data-testid="drive-sync-dialog">
                    <DialogHeader className="space-y-1 mb-5">
                        <DialogTitle className="font-heading font-bold text-xl tracking-tight text-zinc-900">Sync &amp; backup</DialogTitle>
                        <p className="text-sm text-[#71717A]">Back up completed sessions to your Google Drive — nightly, to a private folder.</p>
                    </DialogHeader>
                    <DriveConnect />
                </DialogContent>
            </Dialog>
        </aside>
    );
}

// Browser handoff pages for desktop Google Drive OAuth (the macOS webview
// can't run Google OAuth, so the desktop app opens these in the system
// browser). /drive-connect kicks off the consent redirect; /drive-callback
// bounces the auth code back into the app via the lisn://drive deep link.
function DriveCenter({ children }) {
    return (
        <div className="min-h-screen flex items-center justify-center p-8 text-center bg-[#FBFBFC]">
            <div className="max-w-sm">
                <div className="inline-flex items-center gap-2.5 mb-6">
                    <span className="w-6 h-6 bg-[#0A0A0A] rounded-md flex items-center justify-center">
                        <span className="w-1.5 h-1.5 bg-[#FF3333] rounded-full" />
                    </span>
                    <span className="font-heading font-extrabold text-[16px] tracking-tight">Lisn<span className="text-[#FF3333]">.</span></span>
                </div>
                {children}
            </div>
        </div>
    );
}

function DriveOAuthInitiate() {
    useEffect(() => {
        const params = new URLSearchParams({
            client_id: GOOGLE_CLIENT_ID,
            redirect_uri: `${window.location.origin}/drive-callback`,
            response_type: "code",
            scope: "https://www.googleapis.com/auth/drive.file",
            access_type: "offline",
            prompt: "consent",
            include_granted_scopes: "true",
            state: "desktop",
        });
        window.location.assign(`https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`);
    }, []);
    return <DriveCenter><p className="text-[15px] text-[#52525B]">Opening Google Drive…</p></DriveCenter>;
}

function DriveOAuthCallback() {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const err = params.get("error");
    useEffect(() => {
        if (code) window.location.href = `lisn://drive?code=${encodeURIComponent(code)}`;
    }, [code]);
    if (err) {
        return <DriveCenter><p className="text-[15px] text-rose-600">Couldn’t connect Drive ({err}). Close this and try again.</p></DriveCenter>;
    }
    return (
        <DriveCenter>
            <h1 className="font-heading font-bold text-xl text-zinc-900 mb-2">Drive connected</h1>
            <p className="text-[14px] text-[#52525B] mb-5">Return to Lisn to finish — it should reopen automatically.</p>
            {code && (
                <a href={`lisn://drive?code=${encodeURIComponent(code)}`} className="inline-flex items-center justify-center h-10 px-5 rounded-full bg-[#0A0A0A] text-white text-[13px] font-semibold">
                    Open Lisn
                </a>
            )}
        </DriveCenter>
    );
}

function App() {
    // The floating companion window loads the same bundle with `?pip=1`. Render
    // ONLY the overlay there — no auth, no router, no app chrome.
    if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("pip") === "1") {
        return <PipOverlay />;
    }
    // Desktop Drive OAuth handoff pages (run in the system browser).
    if (typeof window !== "undefined") {
        const p = window.location.pathname;
        if (p === "/drive-connect") return <DriveOAuthInitiate />;
        if (p === "/drive-callback") return <DriveOAuthCallback />;
    }
    if (!GOOGLE_CLIENT_ID) {
        return (
            <div className="min-h-screen flex items-center justify-center p-8 text-center">
                <div>
                    <h1 className="text-xl font-bold mb-2">Configuration error</h1>
                    <p className="text-sm text-[#52525B]">
                        REACT_APP_GOOGLE_CLIENT_ID is not set. Add it to frontend/.env and restart.
                    </p>
                </div>
            </div>
        );
    }
    return (
        // MotionConfig honours the OS "reduce motion" setting for every
        // framer-motion animation in the tree — the premium-feel baseline.
        <MotionConfig reducedMotion="user">
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                <AuthProvider>
                    <BrowserRouter>
                        <Shell />
                    </BrowserRouter>
                </AuthProvider>
            </GoogleOAuthProvider>
        </MotionConfig>
    );
}

// The signed-in app shell: sidebar rail + a content area inset by the sidebar
// width (--app-left, set to 0 when logged out so the login screen is full-bleed).
function Shell() {
    const { user } = useAuth();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem("lisn.sidebar.collapsed") === "1"; } catch { return false; }
    });
    const toggleSidebar = () => setCollapsed((c) => {
        const next = !c;
        try { localStorage.setItem("lisn.sidebar.collapsed", next ? "1" : "0"); } catch { /* ignore */ }
        return next;
    });
    return (
        <div
            className={`App min-h-screen bg-white ${user ? "has-sidebar" : ""}`}
            style={user ? { "--sidebar-w": collapsed ? "68px" : "220px" } : undefined}
        >
            <Sidebar collapsed={collapsed} onToggle={toggleSidebar} />
            <MeetingReminders />
            <NativeBridge />
            <DriveAutoBackup />
            <main className="ml-[var(--app-left)] min-h-screen">
                {/* Route transition — each navigation lands with a quick,
                    quiet rise-and-settle (enter-only: no exit choreography,
                    so lazy chunks and Suspense stay simple and nothing ever
                    feels slower than an instant app). */}
                <motion.div
                    key={location.pathname}
                    initial={{ opacity: 0, y: 12, scale: 0.996 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.42, ease: [0.22, 0.61, 0.36, 1] }}
                >
                <Suspense fallback={null}>
                <Routes>
                            <Route path="/login" element={<Login />} />
                            <Route
                                path="/"
                                element={
                                    <ProtectedRoute>
                                        <Home />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/sessions/:id"
                                element={
                                    <ProtectedRoute>
                                        <LiveSession />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/sessions/:id/review"
                                element={
                                    <ProtectedRoute>
                                        <Review />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/participants"
                                element={
                                    <ProtectedRoute>
                                        <Participants />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/participants/:name"
                                element={
                                    <ProtectedRoute>
                                        <ParticipantDashboard />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/folders/:id"
                                element={
                                    <ProtectedRoute>
                                        <Folder />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/library"
                                element={
                                    <ProtectedRoute>
                                        <Library />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/brain"
                                element={
                                    <ProtectedRoute>
                                        <Brain />
                                    </ProtectedRoute>
                                }
                            />
                            {/* The old Meetings list is absorbed by Library;
                                redirect to it filtered to meetings. The
                                meeting ROOM keeps its own route below. */}
                            <Route path="/meetings" element={<Navigate to="/library?type=meetings" replace />} />
                            <Route
                                path="/meetings/:id"
                                element={
                                    <ProtectedRoute>
                                        <MeetingRoom />
                                    </ProtectedRoute>
                                }
                            />
                </Routes>
                </Suspense>
                </motion.div>
            </main>
            <Toaster position="top-right" richColors closeButton />
        </div>
    );
}

export default App;
